/**
 * The driver's marker as an SVG data URL, the same art as the apps' map (iOS Cars.xcassets, Android
 * res/drawable). Cars: a sedan seen from above, nose north, turned to the heading; white for light
 * registered colours, black for the rest. Motorbikes: a Brasa disc with an upright helmet and a notch on
 * the ring pointing the way it's heading (none without a heading).
 */
export type VehicleKind = "car" | "motorcycle";

/** Pixel size the marker is drawn at (square). */
export const markerSize = (kind: VehicleKind | undefined) => (kind === "motorcycle" ? 40 : 48);

export function carColor(vehicleColor: string | null | undefined): "white" | "black" {
  const c = (vehicleColor ?? "").toLowerCase().normalize("NFD").replace(/\p{Mn}+/gu, "");
  return ["branc", "white", "prata", "prateado", "silver", "cinza", "gray", "grey", "perola"].some((k) => c.includes(k)) && !c.includes("escur") ? "white" : "black";
}

const PALETTE = {
  // CarTopWhite: a light body outlined in Ink.
  white: { outline: "#15110E", body: "#F4F2EE", glass: "#2A2F36", roof: "#FBFAF8", lights: "#F2C94C" },
  // CarTopBlack: a dark body outlined in white.
  black: { outline: "#FFFFFF", body: "#1E1B19", glass: "#5A6573", roof: "#272320", lights: "#F4F1E8" },
};
const MIRRORS = "M19 20.4L16.4 21.2C15.6 21.5 15.6 23.1 16.4 23.4L19 23.9Z M45 20.4L47.6 21.2C48.4 21.5 48.4 23.1 47.6 23.4L45 23.9Z";
const BODY = "M32 5C39.6 5 43.7 6.7 44.5 11.6C45.1 15.2 45.3 20 45.3 24L45.3 50.6C45.3 56.1 42.6 59 32 59C21.4 59 18.7 56.1 18.7 50.6L18.7 24C18.7 20 18.9 15.2 19.5 11.6C20.3 6.7 24.4 5 32 5Z";

function carSvg(color: "white" | "black", heading: number) {
  const p = PALETTE[color];
  const outline = `fill="${p.outline}" stroke="${p.outline}" stroke-width="3.2" stroke-linejoin="round"`;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
<defs><filter id="s" x="-20%" y="-20%" width="140%" height="140%"><feDropShadow dx="0" dy="0" stdDeviation="2" flood-opacity="0.3"/></filter></defs>
<g transform="rotate(${heading} 32 32)" filter="url(#s)">
<path d="${MIRRORS}" ${outline}/><path d="${BODY}" ${outline}/>
<path d="${MIRRORS}" fill="${p.body}"/><path d="${BODY}" fill="${p.body}"/>
<path d="M21.4 21.4C25.2 18.7 38.8 18.7 42.6 21.4L40.7 28.7C36 27.7 28 27.7 23.3 28.7Z" fill="${p.glass}"/>
<path d="M23.7 29.5C28 28.6 36 28.6 40.3 29.5L40.3 42.4C36 43.2 28 43.2 23.7 42.4Z" fill="${p.roof}"/>
<path d="M23.5 43.4C28 44.2 36 44.2 40.5 43.4L42.2 49.4C38 51.2 26 51.2 21.8 49.4Z" fill="${p.glass}"/>
<path d="M21.2 23.6L22.7 29.6L22.7 42.2L21.1 47.4C20.5 40 20.5 30.2 21.2 23.6Z M42.8 23.6L41.3 29.6L41.3 42.2L42.9 47.4C43.5 40 43.5 30.2 42.8 23.6Z" fill="${p.glass}"/>
<path d="M20.6 10C21.6 7.9 24 6.8 26.4 6.5L25.7 8.5C23.9 8.9 22.4 9.8 21.2 11.1Z M43.4 10C42.4 7.9 40 6.8 37.6 6.5L38.3 8.5C40.1 8.9 41.6 9.8 42.8 11.1Z" fill="${p.lights}"/>
<path d="M19.3 53.8C20.1 56.3 22.2 57.6 25 58.1L25 56.6C22.9 56.2 21.2 55.2 20.3 53.4Z M44.7 53.8C43.9 56.3 41.8 57.6 39 58.1L39 56.6C41.1 56.2 42.8 55.2 43.7 53.4Z" fill="#E5352B"/>
</g></svg>`;
}

const notchPath = (heading: number) => `<path d="M18 0.6L22 6.4L14 6.4Z" fill="#15110E" transform="rotate(${heading} 18 18)"/>`;

/** MotoMarker on its 36-point grid: notch, ringed disc, the 24-point helmet scaled to 19.8. */
function motoSvg(heading: number | null) {
  const notch = heading === null ? "" : notchPath(heading);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
<defs><filter id="s" x="-30%" y="-30%" width="160%" height="160%"><feDropShadow dx="0" dy="1" stdDeviation="1" flood-opacity="0.25"/></filter></defs>
${notch}<circle cx="18" cy="18" r="12.5" fill="#FF6B1A" stroke="#FFFFFF" stroke-width="2.5" filter="url(#s)"/>
<g transform="translate(8.1 8.1) scale(0.825)">
<path d="M4 16.5C3.4 10 7.4 4.8 13.2 4.8C18.2 4.8 21.2 8.4 21.2 12.6L21.2 15.6C21.2 16.7 20.4 17.4 19.3 17.4L13.6 17.4C12.9 17.4 12.4 17.8 12.1 18.4L11.6 19.2L5.6 19.2C4.7 19.2 4.1 18.1 4 16.5Z" fill="#1A0D04"/>
<path d="M11.8 10.6C11.8 9.9 12.3 9.5 13 9.5L19.6 9.5C20.6 9.9 21.2 11 21.2 12.6L21.2 14.2L13 14.2C12.3 14.2 11.8 13.8 11.8 13.1Z" fill="#FFE2CC"/>
<path d="M6.2 11.2C7.4 8.2 9.8 6.5 12.6 6.3" fill="none" stroke="#FFFFFF" stroke-width="1.6" stroke-linecap="round"/>
</g></svg>`;
}

/** Headings snap to 5° so the marker's URL (and image) only changes when the turn is visible. */
const snap = (h: number) => (Math.round((((h % 360) + 360) % 360) / 5) * 5) % 360;

/** Just the moto marker's notch, pointing north: for the site's map, which turns it without swapping the image. */
export const motoNotch = () =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">${notchPath(0)}</svg>`)}`;

export function vehicleMarker(kind: VehicleKind | undefined, vehicleColor: string | null | undefined, heading: number | null | undefined): string {
  const svg = kind === "motorcycle" ? motoSvg(heading == null ? null : snap(heading)) : carSvg(carColor(vehicleColor), snap(heading ?? 0));
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}
